import { useMutation } from '@apollo/client/react';
import { useIonRouter, useIonToast } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { LEAVE_FANTASY_LEAGUE } from '../../../../graphql/mutations/leaveFantasyLeague';
import { GET_MY_FANTASY_LEAGUES } from '../../../../graphql/queries/myFantasyLeagues';
import { useErrorToast } from '../../../../hooks/useErrorToast';
import type {
  LeaveFantasyLeagueMutation,
  LeaveFantasyLeagueMutationVariables,
} from '../../../../types/graphql-generated';

const LEAVE_ERROR_MESSAGE = 'No se ha podido abandonar la liga';

// Leaves the league for the current user. On success it drops the league from
// the Apollo cache, refreshes "Mis ligas" and navigates there as the new root,
// so the back button can't return to the league's screens.
export const useLeaveLeague = (leagueId: string) => {
  const router = useIonRouter();
  const [presentToast] = useIonToast();
  const showError = useErrorToast();

  const [leaveFantasyLeague, { loading: leaving }] = useMutation<
    LeaveFantasyLeagueMutation,
    LeaveFantasyLeagueMutationVariables
  >(LEAVE_FANTASY_LEAGUE, {
    refetchQueries: [{ query: GET_MY_FANTASY_LEAGUES }],
  });

  const leaveLeague = async () => {
    try {
      const result = await leaveFantasyLeague({
        variables: { input: { fantasyLeagueId: leagueId } },
        update: (cache, mutationResult) => {
          if (!mutationResult.data?.leaveFantasyLeague?.success) return;
          cache.evict({ id: cache.identify({ __typename: 'FantasyLeague', id: leagueId }) });
          cache.gc();
        },
      });
      const payload = result.data?.leaveFantasyLeague;
      if (!payload?.success) {
        showError(payload?.errors?.[0] ?? LEAVE_ERROR_MESSAGE);
        return;
      }
      presentToast({
        message: 'Has abandonado la liga',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'app-footer',
        cssClass: 'toast-success',
        buttons: [{ icon: closeOutline, role: 'cancel' }],
      });
      router.push('/app/main/mis-ligas', 'root', 'replace');
    } catch {
      showError(LEAVE_ERROR_MESSAGE);
    }
  };

  return { leaveLeague, leaving };
};
